import { useEffect, useState } from 'react'
import { Button, Checkbox, FormSection, HelpTip, Radio, Select, TextField } from './ui'
import { FileTile } from './FileTile'
import { referralsApi, type ReferrerOption } from '../api/referrals'
import { checkIban, lookupAbiCab } from '../lib/iban'
import { cfNamesCheck } from '../lib/codiceFiscale'
import { SDD_HOLDER_KEYS, isPersonFiscalCode, sddHolderFromCustomer } from '../lib/sddHolder'
import { PAYMENT_LABELS, SIGNED_PDA_KEY, type OperatorPortalConfig, type PortalModule } from '../lib/operatorPortalConfig'
import { missingProfileFields, visibleFields } from '../lib/portalProfiles'
import { indirizzoAttivazione } from '../lib/skyPromo'
import { SkyCoperturaField, SkyPromoField } from './SkyPromoField'
import { ERMES_OPERAZIONI_GAS, ERMES_OPERAZIONI_LUCE, OPERAZIONE_NESSUNA, isRiattivazione, operazioniRisposte, operazioniScelte, toggleOperazione, type ErmesOperazione } from '../lib/ermesOperazioni'

/**
 * Dati della pratica richiesti dai portali degli operatori (le chiavi sono quelle dei campi di Ermes, cosi' il
 * caricamento sul portale li copia senza conversioni). Valori sempre stringa; le caselle valgono 'SI' o ''.
 */
type Opt = { value: string; label: string }
type Field =
  | { key: string; label: string; type: 'text'; maxLength?: number; inputType?: string; hint?: string }
  | { key: string; label: string; type: 'select'; options: Opt[] }
  | { key: string; label: string; type: 'radio'; options: Opt[] }
  | { key: string; label: string; type: 'checkbox' }
  // Più scelte in un solo campo (codici separati da virgola), con una voce esclusiva "Nessuna".
  | { key: string; label: string; type: 'multi'; options: ErmesOperazione[]; hint?: string }

/**
 * Cliente Ermes che presenta l'amico: si SCEGLIE tra i clienti del CRM (ricerca per nome, ragione sociale o P.IVA, prima
 * quelli con un contratto Ermes). Nei dati portale finiscono il nome (PORTA_UN_AMICO_SEGNALATORE) e l'id del cliente
 * (PORTA_UN_AMICO_SEGNALATORE_ID): il caricamento della bozza usa l'id, senza indovinare dal nome. Una pratica vecchia
 * con il solo nome scritto a mano lo mostra e propone subito i clienti che corrispondono.
 */
function SegnalatoreField({ id, label, hint, value, selectedId, disabled, onPick }: {
  id: string; label: string; hint?: string | undefined; value: string; selectedId: string; disabled?: boolean | undefined
  onPick: (name: string, customerId: string) => void
}) {
  const [query, setQuery] = useState(selectedId ? '' : value)
  const [found, setFound] = useState<ReferrerOption[]>([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) { setFound([]); setMsg(null); return }
    let alive = true
    setBusy(true)
    const t = setTimeout(() => {
      referralsApi.searchReferrers(q)
        .then(r => {
          if (!alive) return
          setFound(r.items)
          setMsg(r.items.length === 0 ? 'Nessun cliente del CRM corrisponde: controlla il nome, oppure crea prima il cliente.' : null)
        })
        .catch(e => { if (alive) { setFound([]); setMsg(e instanceof Error ? e.message : 'Ricerca non riuscita.') } })
        .finally(() => { if (alive) setBusy(false) })
    }, 250)
    return () => { alive = false; clearTimeout(t) }
  }, [query])
  if (selectedId) {
    return (
      <div>
        <span className="d-block md-typescale-body-small text-muted mb-1">{label}</span>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <strong>{value}</strong>
          <span className="md-typescale-body-small text-muted">cliente n. {selectedId}</span>
          {!disabled && (
            <Button type="button" variant="text" size="sm" onClick={() => { onPick('', ''); setQuery('') }}>Cambia</Button>
          )}
        </div>
        {hint && <span className="d-block md-typescale-body-small text-muted mt-1">{hint}</span>}
      </div>
    )
  }
  const supporting = msg ?? (busy ? 'Cerco…' : value ? `Scritto a mano: «${value}». Scegli il cliente dall'elenco per essere sicuro.` : hint)
  return (
    <div>
      <TextField id={id} label={label} value={query} disabled={disabled} autoComplete="off"
        placeholder="Cerca per nome, ragione sociale o P.IVA"
        {...(supporting ? { supportingText: supporting } : {})}
        onChange={e => setQuery(e.target.value)} />
      {found.length > 0 && (
        <div className="d-flex flex-column align-items-start gap-1 mt-1" role="listbox" aria-label="Clienti trovati">
          {found.map(s => (
            <Button key={s.id} type="button" variant={s.ermes ? 'tonal' : 'text'} size="sm"
              onClick={() => { onPick(s.name, String(s.id)); setFound([]); setQuery('') }}>
              {s.name}{s.city ? ` · ${s.city}` : ''}{s.ermes ? ' · cliente Ermes' : ''}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}

const o =(...values: (string | [string, string])[]): Opt[] =>
  values.map(v => (Array.isArray(v) ? { value: v[0], label: v[1] } : { value: v, label: v }))

const text = (key: string, label: string, extra: Partial<Extract<Field, { type: 'text' }>> = {}): Field =>
  ({ key, label, type: 'text', ...extra })
const check = (key: string, label: string): Field => ({ key, label, type: 'checkbox' })

const TIPO_FORNITURA = o(
  'STANDARD', 'ADEGUAMENTO', ['VOLTURA', 'Voltura su punto già attivo'], ['VOLTURA CONTESTUALE', 'Voltura contestuale'],
  ['RICONTRATTUALIZZAZIONE', 'Ricontrattualizzazione'], ['RIATTIVAZIONE', 'Riattivazione'], ['NUOVA POSA', 'Nuova posa'],
)

const ADDRESS = (s: 'CLIENTE' | 'POD' | 'PDR'): Field[] => [
  text(`INDIRIZZO_${s}`, 'Indirizzo', s === 'CLIENTE'
    ? { hint: 'Quello della visura (sede legale) se è allegata, altrimenti dell\'intestazione della bolletta: le letture dei documenti lo aggiornano anche se era già compilato dalla scheda cliente.' }
    : {}),
  text(`NUM_CIVICO_${s}`, 'Civico'),
  text(s === 'CLIENTE' ? 'ID_COMUNE_CLIENTE' : `ID_COMUNE_${s}`, 'Comune'),
  text(`PROVINCIA_${s}`, 'Provincia', { maxLength: 2 }),
  text(`CAP_${s}`, 'CAP', { maxLength: 5 }),
]

const PRIVATO: Field[] = [text('NOME_CLIENTE', 'Nome'), text('COGNOME_CLIENTE', 'Cognome'), text('CODICE_FISCALE_CLIENTE', 'Codice fiscale', { maxLength: 16 })]
const AZIENDA: Field[] = [
  text('RAGIONE_SOCIALE_CLIENTE', 'Ragione sociale'), text('PARTITA_IVA_CLIENTE', 'Partita IVA', { maxLength: 11 }),
  text('CODICE_FISCALE_CLIENTE', 'Codice fiscale'), text('LEGALE_RAPPRESENTANTE_CLIENTE', 'Legale rappresentante'),
  text('NOME_INSEGNA', 'Insegna'), text('SETTORE_MERCEOLOGICO', 'Settore merceologico / ATECO'),
]
const CONTATTI: Field[] = [
  text('TEL_CLIENTE', 'Telefono', { inputType: 'tel' }), text('REFERENTE_CLIENTE', 'Referente'),
  text('EMAIL_SPED', 'Email', { inputType: 'email' }), text('EMAIL_PEC_CLIENTE', 'PEC', { inputType: 'email' }),
  text('SITO_WEB', 'Sito web'),
]

const LUCE: Field[] = [
  { key: 'TIPO_FORNITURA_POD', label: 'Tipo di fornitura', type: 'select', options: TIPO_FORNITURA },
  text('POD', 'POD', { maxLength: 14 }),
  ...ADDRESS('POD'),
  text('POD_POTENZA_IMPEGNATA', 'Potenza impegnata (kW)', { inputType: 'number' }),
  text('POD_POTENZA_DISPONIBILE', 'Potenza disponibile (kW)', { inputType: 'number' }),
  text('POD_CONSUMI_ANNUI', 'Consumo annuo (kWh)', { inputType: 'number' }),
  { key: 'TENSIONE_PERDITATO', label: 'Tensione', type: 'radio', options: o(['BT', 'Bassa (BT)'], ['MT', 'Media (MT)']) },
  { key: 'POD_OPZ_TARIFFARIA', label: 'Opzione tariffaria', type: 'radio', options: o('BT') },
  { key: 'TIPO_TARIFFA', label: 'Tariffa', type: 'select', options: o(['MULTI', 'Multioraria']) },
  text('ID_LISTINO_PREZZI_POD', 'Listino luce (codice portale)'),
  text('SCONTO_LUCE', 'Sconto luce'),
]

const GAS: Field[] = [
  { key: 'TIPO_FORNITURA_PDR', label: 'Tipo di fornitura', type: 'select', options: TIPO_FORNITURA },
  text('PDR', 'PDR', { maxLength: 14 }),
  ...ADDRESS('PDR'),
  text('REMI_PDR', 'Codice REMI'),
  text('MATR_CONT_PDR', 'Matricola contatore'),
  text('PDR_ID_DISTRIBUTORE', 'Distributore gas (nome come su Ermes)', { hint: 'Es. «Italgas Reti», «2i Rete Gas»: è scritto in bolletta. Il caricamento sceglie la voce di Ermes con le stesse parole (punteggiatura e «S.p.A.» non contano); se non la trova, la nota della pratica elenca le voci del portale.' }),
  { key: 'PDR_CLASSE_PRELIEVO', label: 'Classe di prelievo', type: 'select', options: o(['5', '5 giorni'], ['6', '6 giorni'], ['7', '7 giorni']) },
  {
    key: 'PDR_TIPO_USO', label: "Tipo d'uso", type: 'select', options: o(
      ['C1', 'C1 Riscaldamento'], ['C2', 'C2 Cottura cibi e/o acqua calda'], ['C3', 'C3 Riscaldamento + cottura/acqua calda'],
      ['C4', 'C4 Condizionamento'], ['C5', 'C5 Condizionamento + riscaldamento'],
      ['T1', 'T1 Uso tecnologico'], ['T2', 'T2 Uso tecnologico + riscaldamento'],
    ),
  },
  text('PDR_VOLUME_ANNUO', 'Consumo annuo (Smc)', { inputType: 'number' }),
  { key: 'TIPOLOGIA_PDR', label: 'Tipologia PDR', type: 'radio', options: o(['CO', 'Condominio (CO)'], ['AU', 'Autonomo (AU)']) },
  text('ID_LISTINO_PREZZI_PDR', 'Listino gas (codice portale)'),
  text('SCONTO_CMEM_GAS', 'Sconto gas'),
]

/** Ermes: residente/non residente decide tariffa e listino sul portale; le operazioni sul sito stanno nella pagina Allegati. */
const ERMES_RESIDENTE: Field = { key: 'RESIDENTE_POD', label: 'Opzione tariffaria (decide tariffa e listino)', type: 'radio', options: o(['SI', 'Domestico residente'], ['NO', 'Domestico non residente']) }
/** Ermes: la tariffa la propone il listino calcolato dal portale (spesso una sola voce); vuoto = quella del listino. */
const ERMES_TARIFFA: Field = { key: 'TIPO_TARIFFA', label: 'Tariffa', type: 'select', options: o(['MONO', 'Monoraria'], ['MULTI', 'Multioraria']) }
const ERMES_OPERAZIONI = (s: 'POD' | 'PDR'): Field => ({
  key: `OPERAZIONI_${s}`, label: `Operazioni da richiedere sul ${s} (pagina Allegati di Ermes)`, type: 'multi',
  options: s === 'POD' ? ERMES_OPERAZIONI_LUCE : ERMES_OPERAZIONI_GAS,
  hint: 'Anche più di una. Con il subentro (riattivazione) la bolletta non è obbligatoria.',
})
/** Ermes, cliente azienda: il modulo "porta un amico" si allega alla bozza dell'amico (scheda Porta un amico del segnalatore). */
const ERMES_PORTA_AMICO: Field[] = [
  { key: 'PORTA_UN_AMICO', label: 'Porta un amico: il cliente è stato presentato da un altro cliente Ermes?', type: 'radio', options: o(['NO', 'No'], ['SI', 'Sì']) },
  text('PORTA_UN_AMICO_SEGNALATORE', 'Cliente Ermes che lo presenta (ragione sociale)', { hint: 'Il modulo si compila dalla scheda "Porta un amico" del segnalatore e si allega alla bozza.' }),
]

/**
 * Ermes, "Richiesta di Subentro / Preposato" (riattivazione): il portale aggiunge un passo 8 alla pratica con questi campi
 * (nomi del portale). Nome, cognome, CF e residenza dell'intestatario li ricava dai passi 1-5; nascita e dichiarazioni no.
 */
const ERMES_SUBENTRO_POD: Field[] = [
  text('NUMERO_DI_PRESA', 'Numero di presa'), text('POTENZA_RICHIESTA', 'Potenza richiesta (kW)', { inputType: 'number' }),
  text('SCALA_POD', 'Scala'), text('PIANO_POD', 'Piano'), text('INTERNO_POD', 'Interno'),
  { key: 'TIPOLOGIA_POD', label: 'Tipologia fornitura', type: 'radio', options: o(['BASSA_TENSIONE_MONOFASE', 'Bassa tensione monofase'], ['BASSA_TENSIONE_TRIFASE', 'Bassa tensione trifase'], ['MT_MEDIA_TENSIONE', 'Media tensione']) },
  { key: 'TIPO_USO_POD', label: "Tipo d'uso", type: 'radio', options: o(['DOMESTICO_RESIDENTE', 'Domestico residente'], ['DOMESTICO_NON_RESIDENTE', 'Domestico non residente'], ['ALTRI_USI', 'Altri usi']) },
]
/**
 * Ermes, "Voltura contestuale/ordinaria": passo 8 "Voltura Contestuale Luce". Sul portale la tensione si chiama
 * TENSIONE_POD e il tipo d'uso TIPOLOGIA_POD: il caricamento li ricava dai campi del subentro qui sopra (stessi
 * significati), quindi nel CRM si compilano gli stessi campi.
 */
const ERMES_VOLTURA: Field[] = [
  { key: 'TIPOLOGIA_VOLTURA', label: 'Tipologia voltura', type: 'radio', options: o(['ORDINARIA', 'Ordinaria'], ['MORTIS_CAUSA', 'Mortis causa'], ['INCORPORAZIONE', 'Incorporazione societaria']) },
]
/**
 * Ermes, modulo «Voltura Contestuale Gas» / subentro gas (30/10/2026): il portale chiede la lettura del contatore e la
 * data. La tipologia del PDR del modulo (domestico, condominio con uso domestico, usi diversi) la ricava il caricamento
 * dal tipo cliente e dalla tipologia PDR della sezione Gas.
 */
const ERMES_VOLTURA_GAS: Field[] = [
  text('RILEVAZIONE_MISURA', 'Lettura del contatore gas (Smc)', { inputType: 'number', hint: '«Rilevazione dati di misura» del modulo gas: la lettura attuale del contatore.' }),
  text('DATA_RILEVAZIONE_MISURA', 'Data della lettura', { hint: 'gg/mm/aaaa' }),
]
/**
 * Ermes, "Aumento/Diminuzione potenza": passo 8 "Aumento Potenza Luce". La nuova potenza è una scelta a codici del
 * portale (kW impegnata/disponibile); oltre 17 kW e in media tensione i kW si scrivono a mano. Telefono ed email li
 * prende dal cliente; scala, piano e interno della fornitura dai campi del subentro qui sopra.
 */
const POTENZE: [string, string, string][] = [
  ['BTA1_1', '0,5', '0,6'], ['BTA1_2', '1', '1,1'], ['BTA1_3', '1,5', '1,7'],
  ['BTA2_1', '2', '2,2'], ['BTA2_2', '2,5', '2,8'], ['BTA2_3', '3', '3,3'],
  ['BTA3_1', '3,5', '3,9'], ['BTA3_2', '4', '4,4'], ['BTA3_3', '4,5', '5'], ['BTA3_4', '5', '5,5'], ['BTA3_5', '5,5', '6,1'], ['BTA3_6', '6', '6,6'],
  ['BTA4_1', '7', '7,7'], ['BTA4_2', '8', '8,8'], ['BTA4_3', '9', '9,9'], ['BTA4_4', '10', '11'],
  ['BTA5_1', '15', '16,5'], ['BTA6_1', '17', '17'],
]
const ERMES_POTENZA: Field[] = [
  {
    key: 'POTENZA_NUOVA', label: 'Nuova potenza totale richiesta', type: 'select',
    options: o(...POTENZE.map(([c, imp, disp]): [string, string] => [c, `${imp} kW (disponibile ${disp})`]), ['BTA6_2', 'Oltre 17 kW (indica i kW sotto)'], ['MT', 'Media tensione (indica i kW sotto)']),
  },
  text('POTENZA_NUOVA_IMPEGNATA', 'kW impegnata (solo oltre 17 kW o MT)', { inputType: 'number' }),
  text('POTENZA_NUOVA_DISPONIBILE', 'kW disponibile (solo oltre 17 kW o MT)', { inputType: 'number' }),
  { key: 'TENSIONE', label: 'Tensione', type: 'radio', options: o(['MONOFASE', 'Monofase'], ['TRIFASE', 'Trifase']) },
]
/**
 * Ermes, "Allegato A / multisito energia elettrica" (27/10/2026): altri POD sullo stesso contratto, oltre al principale
 * della sezione Luce. Il CRM compila il PDF dalla scheda «Moduli Ermes da firmare» con regole fisse (Daniele): numero
 * progressivo = numero di contratto Ermes, nessuna opzione di fatturazione, niente numero di presa, settore sempre
 * Annuale e tariffa sempre B.T.; qui si chiedono solo POD e indirizzo di ogni sito. Firmato e timbrato in basso a destra
 * si ricarica come immagine e il caricamento lo mette nei documenti aggiuntivi di Ermes. Per ogni POD aggiuntivo serve
 * la sua bolletta fra gli allegati (lo controlla la scheda dei moduli).
 */
export const ALLEGATO_A_MAX_SITI = 8
const ALLEGATO_A_SITI: Field = {
  key: 'ALLEGATO_A_SITI', label: 'POD aggiuntivi oltre al principale (Allegato A)', type: 'select',
  options: o(['0', 'Nessuno'], ...Array.from({ length: ALLEGATO_A_MAX_SITI }, (_, i): [string, string] => [String(i + 1), String(i + 1)])),
}
const ALLEGATO_A_SITO = (i: number): Field[] => [
  text(`ALLEGATO_A_${i}_POD`, `POD aggiuntivo ${i}`, { maxLength: 14 }),
  text(`ALLEGATO_A_${i}_INDIRIZZO`, 'Indirizzo'), text(`ALLEGATO_A_${i}_NUM_CIVICO`, 'Civico'),
  text(`ALLEGATO_A_${i}_ID_COMUNE`, 'Comune'), text(`ALLEGATO_A_${i}_PROVINCIA`, 'Provincia', { maxLength: 2 }), text(`ALLEGATO_A_${i}_CAP`, 'CAP', { maxLength: 5 }),
]
/** Quanti POD aggiuntivi (Allegato A) ha la pratica: 0..8. */
export function allegatoASiti(v: Record<string, string>): number {
  const n = parseInt(v['ALLEGATO_A_SITI'] ?? '', 10)
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), ALLEGATO_A_MAX_SITI) : 0
}
const ERMES_SUBENTRO_INTESTATARIO: Field[] = [
  text('LUOGO_NASCITA', 'Nato a (comune)', { hint: 'Se vuoto: dall\'anagrafica o dal codice fiscale.' }), text('DATA_NASCITA', 'Nato il', { hint: 'gg/mm/aaaa; se vuoto: dall\'anagrafica o dal codice fiscale.' }),
  text('SCALA_RESIDENZA_INTEST', 'Scala residenza'), text('PIANO_RESIDENZA_INTEST', 'Piano residenza'), text('INTERNO_RESIDENZA_INTEST', 'Interno residenza'),
]
const ERMES_SUBENTRO_DICHIARAZIONE: Field[] = [
  {
    key: 'DICHIARAZIONE_IMMOBILE', label: "Il cliente dichiara, dell'immobile, di essere", type: 'radio', options: o(
      ['PROPRIETARIO', 'Proprietario'], ['LOCAZIONE', 'Intestatario del contratto di locazione registrato'], ['COMODATARIO', 'Comodatario (comodato d\'uso gratuito registrato)'],
      ['USUFRUTTUARIO', 'Usufruttuario'], ['OCCUPANTE', 'Titolare di diritto d\'uso o d\'abitazione'], ['PARENTELA', 'Parente dell\'avente titolo'], ['DOCUMENTO', 'Altro documento (indicare sotto)'],
    ),
  },
  text('AGENZIA_DELLE_ENTRATE', 'Agenzia delle Entrate di (registrazione)'), text('ALTRO_DOCUMENTO', 'Altro documento'),
  text('SCALA', 'Scala immobile'), text('PIANO', 'Piano immobile'), text('INTERNO', 'Interno immobile'),
  {
    key: 'DICHIARAZIONE_REALIZZAZIONE', label: "L'immobile è stato realizzato", type: 'radio', options: o(
      ['PRIMA_DELLA_DATA', 'Prima del 30/01/1977'], ['PERMESSO_COSTRUIRE', 'Con permesso di costruire'], ['SEGNALAZIONE_CERTIFICATA', 'Con SCIA'], ['AUTORIZZAZIONE', 'Con autorizzazione'],
    ),
  },
  text('REALIZZAZIONE_NUMERO', 'Numero (permesso/SCIA/autorizzazione)'), text('REALIZZAZIONE_DATA', 'In data', { hint: 'gg/mm/aaaa' }), text('REALIZZAZIONE_COMUNE', 'Comune di'),
  text('ID_COMUNE_DOMICILIO', 'Domicilio: comune (se diverso dalla residenza)'), text('INDIRIZZO_DOMICILIO', 'Domicilio: indirizzo'), text('NUM_CIVICO_DOMICILIO', 'Domicilio: civico'), text('CAP_DOMICILIO', 'Domicilio: CAP', { maxLength: 5 }),
]
const ERMES_SUBENTRO_VENETO: Field[] = [
  text('CERTIFICATO_DATA', 'Agibilità (L.R. 61/85) in data', { hint: 'gg/mm/aaaa' }), text('CERTIFICATO_COMUNE', 'Al Comune di'),
  { key: 'CERTIFICATO_REQUISITI', label: 'Certificazione ASL dei requisiti igienico-sanitari', type: 'radio', options: o(['SI', 'Sì'], ['NO', 'No']) },
]
const ERMES_SUBENTRO_TITOLO: Field[] = [
  text('NOME_TITOLO', 'Nome'), text('COGNOME_TITOLO', 'Cognome'), text('COD_COMUNE_NASCITA_TITOLO', 'Nato a (comune)'), text('DATA_NASCITA_TITOLO', 'Nato il', { hint: 'gg/mm/aaaa' }),
  text('ID_COMUNE_RESIDENZA_TITOLO', 'Residenza: comune'), text('INDIRIZZO_RESIDENZA_TITOLO', 'Residenza: indirizzo'), text('NUM_CIVICO_RESIDENZA_TITOLO', 'Residenza: civico'), text('CAP_RESIDENZA_TITOLO', 'Residenza: CAP', { maxLength: 5 }),
  { key: 'DICHIARAZIONE_TITOLO', label: "L'avente titolo dichiara di essere", type: 'radio', options: o(['PROPRIETARIO', 'Proprietario'], ['LOCAZIONE', 'Intestatario della locazione registrata'], ['COMODATARIO', 'Comodatario'], ['DOCUMENTO', 'Altro documento']) },
  text('AGENZIA_DELLE_ENTRATE_TITOLO', 'Agenzia delle Entrate di'), text('ALTRO_DOCUMENTO_TITOLO', 'Altro documento'),
  text('NOME_FIRMATARIO_DICHIARAZIONE', 'Firma digitale: nome'), text('COGNOME_FIRMATARIO_DICHIARAZIONE', 'Firma digitale: cognome'),
  text('EMAIL_FIRMATARIO_DICHIARAZIONE', 'Firma digitale: email', { inputType: 'email' }), text('CELLULARE_FIRMATARIO_DICHIARAZIONE', 'Firma digitale: cellulare', { inputType: 'tel' }),
]

const SDD: Field[] = [
  text('IBAN', 'IBAN', { maxLength: 34 }),
  text('SDD_NOME_FIRMATARIO', 'Nome intestatario conto'), text('SDD_COGNOME_FIRMATARIO', 'Cognome intestatario conto'),
  text('SDD_CF_FIRMATARIO', 'CF intestatario conto', { maxLength: 16 }),
  text('SDD_BANCA', 'Banca'), text('SDD_AGENZIA', 'Agenzia'),
  text('SDD_BANCA_TEL', 'Telefono banca'), text('SDD_BANCA_REFERENTE', 'Referente banca'),
]

/** Telefonia: dove si attiva la linea e cosa si porta dal vecchio operatore. */
const ATTIVAZIONE: Field[] = [
  {
    key: 'TIPO_ATTIVAZIONE', label: 'Tipo di attivazione', type: 'select', options: o(
      ['NUOVA', 'Nuova linea'], ['PORTABILITA', 'Portabilità del numero'], ['MIGRAZIONE', 'Cambio operatore (migrazione)'],
    ),
  },
  { key: 'TIPO_LINEA', label: 'Linea', type: 'select', options: o('FTTH', 'FTTC', 'FWA', 'ADSL', ['MOBILE', 'Mobile']) },
  text('INDIRIZZO_ATTIVAZIONE', 'Indirizzo attivazione'), text('NUM_CIVICO_ATTIVAZIONE', 'Civico'),
  text('COMUNE_ATTIVAZIONE', 'Comune'), text('PROVINCIA_ATTIVAZIONE', 'Provincia', { maxLength: 2 }),
  text('CAP_ATTIVAZIONE', 'CAP', { maxLength: 5 }),
]
const PORTABILITA: Field[] = [
  text('NUMERO_DA_PORTARE', 'Numero da portare', { inputType: 'tel' }),
  text('OPERATORE_ATTUALE', 'Operatore attuale'),
  text('CODICE_MIGRAZIONE', 'Codice di migrazione'),
  text('SERIALE_SIM', 'Seriale SIM (solo mobile)'),
]

/** Etichetta del CRM per un campo del portale (per gli errori di Ermes "da sistemare"); il nome stesso se sconosciuto. */
export function portalFieldLabel(key: string): string {
  const all: Field[] = [
    ...PRIVATO, ...AZIENDA, ...CONTATTI, ...ADDRESS('CLIENTE'), ...LUCE, ...GAS, ...SDD, ...ATTIVAZIONE, ...PORTABILITA,
    ERMES_RESIDENTE, ERMES_TARIFFA, ERMES_OPERAZIONI('POD'), ERMES_OPERAZIONI('PDR'), ...ERMES_PORTA_AMICO,
    ...ERMES_SUBENTRO_POD, ...ERMES_VOLTURA, ...ERMES_VOLTURA_GAS, ...ERMES_POTENZA, ...ERMES_SUBENTRO_INTESTATARIO, ...ERMES_SUBENTRO_DICHIARAZIONE, ...ERMES_SUBENTRO_VENETO, ...ERMES_SUBENTRO_TITOLO,
    ALLEGATO_A_SITI, ...Array.from({ length: ALLEGATO_A_MAX_SITI }, (_, i) => ALLEGATO_A_SITO(i + 1)).flat(),
    check('SPEDIZIONE_EMAIL', 'Bolletta via email'), check('SPEDIZIONE_CARTA', 'Bolletta cartacea'),
    check('FORNITURA_CONTRATTO_LUCE', 'Fornitura luce'), check('FORNITURA_CONTRATTO_GAS', 'Fornitura gas'),
    text('MODO_PAGAMENTO', 'Modalità di pagamento'), text('CONSENSO_PRIVACY', 'Consenso privacy'),
    text('AUTORIZZAZIONE_RIPENSAMENTO', 'Attivazione anticipata'), text('NOTE', 'Note per il portale'),
  ]
  const f = all.find(x => x.key === key)
  if (f) return f.label
  // Campi del passo 8 ricavati dalla pratica (nascita, domicilio): etichette parlanti.
  const derived: Record<string, string> = {
    COD_COMUNE_NASCITA_INTEST: 'Nato a (comune)', DATA_NASCITA_INTEST: 'Nato il', COD_COMUNE_NASCITA_SOST: 'Luogo di nascita (dichiarazione)',
    DATA_NASCITA_SOST: 'Data di nascita (dichiarazione)', PROVINCIA_DOMICILIO: 'Domicilio: provincia', ID_COMUNE_RESIDENZA: 'Residenza: comune',
    // «Il sottoscritto» dei moduli di voltura: il cliente, o il rappresentante legale per un'azienda (dalla scheda cliente).
    NOME: 'Sottoscritto del modulo: nome', COGNOME: 'Sottoscritto del modulo: cognome', CODICE_FISCALE: 'Sottoscritto del modulo: codice fiscale',
    ID_COMUNE: 'Sottoscritto del modulo: comune di residenza', PROVINCIA: 'Sottoscritto del modulo: provincia', CAP: 'Sottoscritto del modulo: CAP',
    INDIRIZZO: 'Sottoscritto del modulo: indirizzo', NUM_CIVICO: 'Sottoscritto del modulo: civico',
  }
  return derived[key] ?? key
}

/** I valori proposti diventano dati della pratica al salvataggio (solo per i portali che li chiedono). */
export function withPortalDefaults(v: Record<string, string>, config: OperatorPortalConfig): Record<string, string> {
  const defaults = config.profile?.defaults ?? config.defaults
  return defaults ? { ...defaults, ...v } : v
}

/**
 * Chiavi obbligatorie (con le condizioni) per dire all'operatore cosa manca prima del caricamento sul portale.
 * `hasBolletta`: bolletta tra gli allegati (undefined = non si sa, nessun controllo); su Ermes è la fattura, obbligatoria
 * salvo riattivazione/subentro.
 */
export function missingPortalFields(v: Record<string, string>, config: OperatorPortalConfig, tipo: string,
  moduleFiles: Record<string, File | null> = {}, hasBolletta?: boolean): string[] {
  const miss: string[] = []
  const need = (k: string, label: string) => { if (!v[k]?.trim()) miss.push(label) }
  if (!tipo) miss.push('Privato o azienda')
  // Ermes rifiuta a pagina 3 un nome/cognome non coerente con il codice fiscale: quasi sempre sono invertiti.
  const cfCheck = cfNamesCheck(v['CODICE_FISCALE_CLIENTE'], v['NOME_CLIENTE'], v['COGNOME_CLIENTE'])
  if (cfCheck === 'swapped') miss.push('Nome e cognome invertiti rispetto al codice fiscale')
  else if (cfCheck === 'mismatch') miss.push('Nome e cognome non corrispondono al codice fiscale')
  // Stesso controllo sul firmatario del conto SDD (30/10/2026: rappresentante legale scritto «Cognome Nome»).
  const sddCheck = cfNamesCheck(v['SDD_CF_FIRMATARIO'], v['SDD_NOME_FIRMATARIO'], v['SDD_COGNOME_FIRMATARIO'])
  if (sddCheck === 'swapped') miss.push('Nome e cognome del firmatario del conto invertiti rispetto al suo codice fiscale')
  else if (sddCheck === 'mismatch') miss.push('Nome e cognome del firmatario del conto non corrispondono al suo codice fiscale')
  if (config.ermes) {
    if (v['FORNITURA_CONTRATTO_LUCE'] === 'SI') {
      // Residente / non residente è un'opzione domestica: alle aziende non si chiede (Daniele, 27/10/2026).
      if (tipo !== 'AZIENDA') need('RESIDENTE_POD', 'Residente o non residente (luce)')
      if (!operazioniRisposte(v['OPERAZIONI_POD'])) miss.push('Operazioni sul POD (o "Nessuna")')
    }
    if (v['FORNITURA_CONTRATTO_GAS'] === 'SI' && !operazioniRisposte(v['OPERAZIONI_PDR'])) miss.push('Operazioni sul PDR (o "Nessuna")')
    if (tipo === 'AZIENDA') {
      need('PORTA_UN_AMICO', 'Porta un amico (sì/no)')
      if (v['PORTA_UN_AMICO'] === 'SI') need('PORTA_UN_AMICO_SEGNALATORE', 'Cliente Ermes che presenta')
    }
    if (hasBolletta === false && !isRiattivazione(v)) miss.push('Bolletta (fattura) tra gli allegati: obbligatoria su Ermes salvo riattivazione/subentro')
    const opsPod = operazioniScelte(v['OPERAZIONI_POD'])
    const subentro = opsPod.includes('SUBENTRO'), voltura = opsPod.some(c => c.startsWith('VOLTURA'))
    // Modulo gas (voltura/subentro sul PDR, 30/10/2026): lettura del contatore e data; la tipologia del PDR del modulo la
    // ricava il caricamento (azienda = usi diversi, condominio con uso domestico, domestico).
    const opsPdr = operazioniScelte(v['OPERAZIONI_PDR'])
    const subentroGas = opsPdr.includes('SUBENTRO'), volturaGas = opsPdr.some(c => c.startsWith('VOLTURA'))
    if (subentroGas || volturaGas) {
      if (volturaGas && !voltura) need('TIPOLOGIA_VOLTURA', 'Voltura: tipologia (ordinaria, mortis causa, incorporazione)')
      need('RILEVAZIONE_MISURA', 'Modulo gas: lettura del contatore')
      need('DATA_RILEVAZIONE_MISURA', 'Modulo gas: data della lettura')
      if (!(subentro || voltura)) need('DICHIARAZIONE_IMMOBILE', `${subentroGas ? 'Subentro' : 'Voltura'} gas: titolo sull'immobile`)
    }
    if (subentro || voltura) {
      // Passo 8 del portale (subentro / voltura). Nascita: se manca qui, il caricamento la ricava dall'anagrafica o dal
      // codice fiscale (data e codice catastale). Il tipo d'uso, se vuoto, dal residente/non residente.
      const p = subentro ? 'Subentro' : 'Voltura'
      if (voltura) need('TIPOLOGIA_VOLTURA', 'Voltura: tipologia (ordinaria, mortis causa, incorporazione)')
      need('TIPOLOGIA_POD', `${p}: tipologia fornitura`)
      if (subentro) need('TIPO_USO_POD', "Subentro: tipo d'uso")
      need('DICHIARAZIONE_IMMOBILE', `${p}: titolo sull'immobile`)
      if (subentro) need('DICHIARAZIONE_REALIZZAZIONE', 'Subentro: realizzazione immobile')
      if (v['DICHIARAZIONE_IMMOBILE'] === 'PARENTELA') { need('NOME_TITOLO', `${p}: nome avente titolo`); need('COGNOME_TITOLO', `${p}: cognome avente titolo`) }
    }
    if (opsPod.some(c => c.endsWith('_POTENZA'))) {
      need('POTENZA_NUOVA', 'Potenza: nuova potenza totale richiesta'); need('TENSIONE', 'Potenza: tensione (monofase/trifase)')
      if (v['POTENZA_NUOVA'] === 'BTA6_2' || v['POTENZA_NUOVA'] === 'MT') { need('POTENZA_NUOVA_IMPEGNATA', 'Potenza: kW impegnata'); need('POTENZA_NUOVA_DISPONIBILE', 'Potenza: kW disponibile') }
    }
    // Allegato A: ogni POD aggiuntivo con codice, indirizzo e comune (la bolletta per POD la controlla la scheda dei moduli).
    if (v['FORNITURA_CONTRATTO_LUCE'] === 'SI') {
      const n = allegatoASiti(v)
      for (let i = 1; i <= n; i++) {
        if ((v[`ALLEGATO_A_${i}_POD`] ?? '').replace(/\s/g, '').length !== 14) miss.push(`Allegato A: POD aggiuntivo ${i} (14 caratteri)`)
        need(`ALLEGATO_A_${i}_INDIRIZZO`, `Allegato A: indirizzo del POD aggiuntivo ${i}`)
        need(`ALLEGATO_A_${i}_ID_COMUNE`, `Allegato A: comune del POD aggiuntivo ${i}`)
      }
    }
  }
  if (config.profile) {
    // Portale con modulo censito: gli obbligatori sono quelli del modulo.
    miss.push(...missingProfileFields(config.profile, v, tipo))
    if (v['MODO_PAGAMENTO'] === 'SDD' && v['IBAN']?.trim() && !checkIban(v['IBAN']).valid) miss.push('IBAN non valido')
    return miss
  }
  if (tipo === 'AZIENDA') { need('RAGIONE_SOCIALE_CLIENTE', 'Ragione sociale'); need('PARTITA_IVA_CLIENTE', 'Partita IVA') }
  else { need('NOME_CLIENTE', 'Nome'); need('COGNOME_CLIENTE', 'Cognome'); need('CODICE_FISCALE_CLIENTE', 'Codice fiscale') }
  need('INDIRIZZO_CLIENTE', 'Indirizzo cliente'); need('ID_COMUNE_CLIENTE', 'Comune cliente'); need('CAP_CLIENTE', 'CAP cliente')
  need('TEL_CLIENTE', 'Telefono')
  if (config.kind === 'telco') {
    need('TIPO_ATTIVAZIONE', 'Tipo di attivazione'); need('INDIRIZZO_ATTIVAZIONE', 'Indirizzo attivazione')
    if (v['TIPO_ATTIVAZIONE'] === 'PORTABILITA' || v['TIPO_ATTIVAZIONE'] === 'MIGRAZIONE') need('NUMERO_DA_PORTARE', 'Numero da portare')
    if (v['TIPO_ATTIVAZIONE'] === 'MIGRAZIONE') need('CODICE_MIGRAZIONE', 'Codice di migrazione')
    if (config.technologies && v['TIPO_LINEA'] !== 'MOBILE') need('TECNOLOGIA', 'Tecnologia')
  } else {
    if (v['FORNITURA_CONTRATTO_LUCE'] !== 'SI' && v['FORNITURA_CONTRATTO_GAS'] !== 'SI') miss.push('Fornitura luce o gas')
    if (v['FORNITURA_CONTRATTO_LUCE'] === 'SI') { need('POD', 'POD'); need('TIPO_FORNITURA_POD', 'Tipo fornitura luce') }
    if (v['FORNITURA_CONTRATTO_GAS'] === 'SI') { need('PDR', 'PDR'); need('TIPO_FORNITURA_PDR', 'Tipo fornitura gas') }
  }
  if (config.payments.length > 0 && !config.payments.includes(v['MODO_PAGAMENTO'] ?? '')) miss.push('Modalità di pagamento')
  if (v['MODO_PAGAMENTO'] === 'SDD') {
    need('IBAN', 'IBAN')
    if (v['IBAN']?.trim() && !checkIban(v['IBAN']).valid) miss.push('IBAN non valido')
  }
  for (const m of activeModules(v, config)) if (!moduleFiles[m.flag]) miss.push(`Modulo firmato: ${m.label}`)
  need('CONSENSO_PRIVACY', 'Consenso privacy')
  return miss
}

/** Opzioni proponibili per le forniture scelte. */
export function offeredModules(v: Record<string, string>, config: OperatorPortalConfig): PortalModule[] {
  return config.modules.filter(m => !m.supply
    || (m.supply === 'luce' && v['FORNITURA_CONTRATTO_LUCE'] === 'SI')
    || (m.supply === 'gas' && v['FORNITURA_CONTRATTO_GAS'] === 'SI'))
}

/** Opzioni spuntate: ognuna chiede il suo modulo firmato. */
export function activeModules(v: Record<string, string>, config: OperatorPortalConfig): PortalModule[] {
  return offeredModules(v, config).filter(m => v[m.flag] === 'SI')
}

export function PraticaPortalSections({ values, onChange, disabled, filesDisabled, config, tipo, moduleFiles, onModuleFile }: {
  values: Record<string, string>
  onChange: (key: string, value: string) => void
  disabled?: boolean
  /** Per la PDA firmata: di default come `disabled` (nel dettaglio si carica anche senza "Modifica dati"). */
  filesDisabled?: boolean
  config: OperatorPortalConfig
  /** PRIVATO o AZIENDA, dalla scelta unica fatta accanto all'operatore. */
  tipo: string
  moduleFiles: Record<string, File | null>
  onModuleFile: (flag: string, file: File | null) => void
}) {
  const v = (k: string) => values[k] ?? config.profile?.defaults?.[k] ?? config.defaults?.[k] ?? ''

  const render = (f: Field) => {
    const id = `pp-${f.key}`
    if (f.type === 'multi') {
      const cur = v(f.key)
      const on = (code: string) => cur.split(',').map(s => s.trim()).includes(code)
      return (
        <div key={f.key} style={{ gridColumn: '1 / -1' }}>
          <span className="d-block md-typescale-body-small text-muted mb-1">{f.label}</span>
          <div className="d-flex flex-wrap gap-3">
            <Checkbox id={`${id}-${OPERAZIONE_NESSUNA}`} label="Nessuna" checked={on(OPERAZIONE_NESSUNA)} disabled={disabled}
              onChange={e => onChange(f.key, toggleOperazione(cur, OPERAZIONE_NESSUNA, e.target.checked))} />
            {f.options.map(opt => (
              <Checkbox key={opt.code} id={`${id}-${opt.code}`} label={opt.label} checked={on(opt.code)} disabled={disabled}
                onChange={e => onChange(f.key, toggleOperazione(cur, opt.code, e.target.checked))} />
            ))}
          </div>
          {f.hint && <span className="d-block md-typescale-body-small text-muted mt-1">{f.hint}</span>}
        </div>
      )
    }
    if (f.type === 'checkbox') {
      return (
        <div key={f.key} className="d-flex align-items-center">
          <Checkbox id={id} label={f.label} checked={v(f.key) === 'SI'} disabled={disabled}
            onChange={e => onChange(f.key, e.target.checked ? 'SI' : '')} />
        </div>
      )
    }
    if (f.type === 'radio') {
      return (
        <div key={f.key}>
          <span className="d-block md-typescale-body-small text-muted mb-1">{f.label}</span>
          <div className="d-flex flex-wrap gap-3">
            {f.options.map(opt => (
              <Radio key={opt.value} id={`${id}-${opt.value}`} name={f.key} value={opt.value} label={opt.label}
                checked={v(f.key) === opt.value} disabled={disabled} onChange={() => onChange(f.key, opt.value)} />
            ))}
          </div>
        </div>
      )
    }
    if (f.type === 'select') {
      if (f.key === 'SKY_COPERTURA_RETE') {
        // Sky: la rete di copertura si verifica a mano su sky.it; il pulsante apre la pagina con l'indirizzo già copiato.
        const hint = (f as { hint?: string }).hint
        return (
          <SkyCoperturaField key={f.key} id={id} label={f.label} hint={hint} value={v(f.key)} options={f.options}
            indirizzo={indirizzoAttivazione(values)} disabled={disabled} onChange={val => onChange(f.key, val)} />
        )
      }
      return (
        <div key={f.key}>
          <Select id={id} label={f.label} value={v(f.key)} disabled={disabled} onChange={e => onChange(f.key, e.target.value)}>
            <option value="">—</option>
            {f.options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </Select>
        </div>
      )
    }
    if (f.key === 'SKY_ID_PROMO') {
      // Sky: l'ID promo dal canvass PROMO SKY letto dal server, filtrato per cosa si attiva, tipo cliente e rete trovata.
      return (
        <SkyPromoField key={f.key} id={id} label={f.label} hint={f.hint} value={v(f.key)} disabled={disabled}
          contesto={{ tipologia: v('SKY_TIPOLOGIA'), tipo, rete: v('SKY_COPERTURA_RETE') }}
          onChange={val => onChange(f.key, val)} />
      )
    }
    if (f.key === 'IBAN') {
      const c = checkIban(v('IBAN'))
      const info = !c.iban ? f.hint : c.valid ? `IBAN valido${c.abi ? ` · ABI ${c.abi} · CAB ${c.cab}` : ''}` : undefined
      return (
        <div key={f.key}>
          <TextField id={id} label={f.label} value={v('IBAN')} disabled={disabled} maxLength={42} uppercase
            {...(c.iban && !c.valid ? { error: 'IBAN non valido' } : {})}
            {...(info ? { supportingText: info } : {})}
            onChange={e => {
              onChange('IBAN', e.target.value)
              // Banca e agenzia da ABI/CAB, solo se i campi sono ancora vuoti.
              const n = checkIban(e.target.value)
              if (n.valid && n.abi) {
                const emptyBank = !v('SDD_BANCA').trim(), emptyAgency = !v('SDD_AGENZIA').trim()
                void lookupAbiCab(n.abi, n.cab).then(r => {
                  if (emptyBank && r.bank) onChange('SDD_BANCA', r.bank)
                  if (emptyAgency && r.agency) onChange('SDD_AGENZIA', r.agency)
                })
              }
            }}
            onBlur={() => { if (c.valid && c.iban !== v('IBAN')) onChange('IBAN', c.iban) }} />
        </div>
      )
    }
    if (f.key === 'SDD_CF_FIRMATARIO') {
      // Ermes rifiuta a pagina 3 un firmatario del conto senza codice fiscale di persona (la P.IVA non vale) e un nome o
      // cognome non coerente con il codice (30/10/2026: rappresentante legale scritto «Cognome Nome»).
      const cf = v(f.key).trim()
      const ordine = cfNamesCheck(cf, v('SDD_NOME_FIRMATARIO'), v('SDD_COGNOME_FIRMATARIO'))
      const warn = cf && tipo === 'AZIENDA' && !isPersonFiscalCode(cf)
        ? 'Non è il codice fiscale di una persona: Ermes vuole quello del rappresentante legale. Al caricamento il CRM usa quello della scheda cliente, se c\'è.'
        : ordine === 'swapped'
        ? 'Nome e cognome del firmatario sono invertiti rispetto a questo codice fiscale: al caricamento il CRM li scambia, ma correggili qui e nel rappresentante legale della scheda cliente.'
        : ordine === 'mismatch'
        ? 'Nome e cognome del firmatario non corrispondono a questo codice fiscale: Ermes lo rifiuta a pagina 3.'
        : undefined
      return (
        <div key={f.key}>
          <TextField id={id} label={f.label} value={v(f.key)} disabled={disabled} uppercase
            {...(f.maxLength ? { maxLength: f.maxLength } : {})}
            {...(warn ? { error: warn } : f.hint ? { supportingText: f.hint } : {})}
            onChange={e => onChange(f.key, e.target.value)} />
        </div>
      )
    }
    if (f.key === 'PORTA_UN_AMICO_SEGNALATORE') {
      return (
        <SegnalatoreField key={f.key} id={id} label={f.label} hint={f.hint} value={v(f.key)}
          selectedId={v('PORTA_UN_AMICO_SEGNALATORE_ID')} disabled={disabled}
          onPick={(name, customerId) => { onChange(f.key, name); onChange('PORTA_UN_AMICO_SEGNALATORE_ID', customerId) }} />
      )
    }
    return (
      <div key={f.key}>
        <TextField id={id} label={f.label} type={f.inputType ?? 'text'} value={v(f.key)} disabled={disabled}
          {...(f.maxLength ? { maxLength: f.maxLength } : {})}
          {...(f.hint ? { supportingText: f.hint } : {})}
          onChange={e => onChange(f.key, e.target.value)} />
      </div>
    )
  }

  // Pagamento: tasto che riempie l'intestatario del conto (nome, cognome, CF) con i dati anagrafici della pratica.
  const copyHolder = () => { for (const [k, val] of Object.entries(sddHolderFromCustomer(values, tipo))) onChange(k, val) }
  const copyHolderButton = (
    <div key="__copy-holder" className="d-flex align-items-end">
      <Button type="button" variant="tonal" size="sm" icon="content_copy" disabled={disabled} onClick={copyHolder}>
        Copia dai dati cliente
      </Button>
    </div>
  )

  const pdaSection = config.signedPda && (
    <FormSection title="PDA firmata" className="mt-4" columns={1}
      description="Se il contratto è cartaceo, la PDA firmata dal cliente in PDF. Facoltativa: senza, su Teamwork risulta PDA digitale.">
      <div className="ecrm-filetiles">
        <FileTile label="PDA firmata" file={moduleFiles[SIGNED_PDA_KEY] ?? null} disabled={filesDisabled ?? disabled ?? false}
          onFile={f => onModuleFile(SIGNED_PDA_KEY, f)} onClear={() => onModuleFile(SIGNED_PDA_KEY, null)} />
      </div>
    </FormSection>
  )

  if (config.profile) {
    const p = config.profile
    return (
      <>
        {visibleFields(p, values, tipo).map(({ section, fields }, i) => (
          <FormSection key={section.title} title={section.title} className="mt-4"
            {...(i === 0 ? { description: section.description ?? `Campi del modulo ${p.portal}.` } : section.description ? { description: section.description } : {})}>
            {fields.map(f => render(f as Field))}
            {fields.some(f => (SDD_HOLDER_KEYS as readonly string[]).includes(f.key)) && copyHolderButton}
          </FormSection>
        ))}
        {pdaSection}
      </>
    )
  }

  const energia = config.kind === 'energia'
  const luce = v('FORNITURA_CONTRATTO_LUCE') === 'SI'
  const gas = v('FORNITURA_CONTRATTO_GAS') === 'SI'
  const opsPod = operazioniScelte(v('OPERAZIONI_POD'))
  const subentro = !!config.ermes && luce && opsPod.includes('SUBENTRO')
  const voltura = !!config.ermes && luce && opsPod.some(c => c.startsWith('VOLTURA'))
  // Gas: le stesse operazioni sul PDR aggiungono il modulo gas (30/10/2026: prima la sezione compariva solo per la luce).
  const opsPdr = operazioniScelte(v('OPERAZIONI_PDR'))
  const subentroGas = !!config.ermes && gas && opsPdr.includes('SUBENTRO')
  const volturaGas = !!config.ermes && gas && opsPdr.some(c => c.startsWith('VOLTURA'))
  const potenza = !!config.ermes && luce && opsPod.some(c => c.endsWith('_POTENZA'))
  const allegatoA = config.ermes && luce ? allegatoASiti(values) : 0
  const attivazione = v('TIPO_ATTIVAZIONE')
  const modules = offeredModules(values, config)
  const active = modules.filter(m => v(m.flag) === 'SI')

  return (
    <>
      <FormSection title="Intestatario" className="mt-4"
        description="Dati del cliente come li chiede il portale dell'operatore.">
        {(tipo === 'AZIENDA' ? AZIENDA : PRIVATO).map(render)}
        {ADDRESS('CLIENTE').map(render)}
        {CONTATTI.map(render)}
        {config.billDelivery && render(check('SPEDIZIONE_EMAIL', 'Bolletta via email'))}
        {config.billDelivery && render(check('SPEDIZIONE_CARTA', 'Bolletta cartacea'))}
        {config.ermes && tipo === 'AZIENDA' && render(ERMES_PORTA_AMICO[0]!)}
        {config.ermes && tipo === 'AZIENDA' && v('PORTA_UN_AMICO') === 'SI' && render(ERMES_PORTA_AMICO[1]!)}
      </FormSection>

      {energia ? (
        <>
          <FormSection title="Fornitura" className="mt-4" description="Scegli luce, gas o entrambi.">
            {render(check('FORNITURA_CONTRATTO_LUCE', 'Luce'))}
            {render(check('FORNITURA_CONTRATTO_GAS', 'Gas'))}
          </FormSection>
          {luce && (
            <FormSection title="Luce" className="mt-3" headingLevel={4}>
              {/* Ermes: l'opzione tariffaria del portale è "Domestico residente / NON residente" (campo qui sotto), solo per i privati. */}
              {LUCE.filter(f => !(config.ermes && (f.key === 'POD_OPZ_TARIFFARIA' || f.key === 'TIPO_TARIFFA'))).map(render)}
              {config.ermes && tipo !== 'AZIENDA' && render(ERMES_RESIDENTE)}
              {config.ermes && render(ERMES_TARIFFA)}
              {config.ermes && (
                <span className="md-typescale-body-small text-muted align-self-center">
                  Tariffa: lasciala vuota per prendere quella del listino Ermes (di solito una sola, Monoraria); se ne scegli una che il listino non prevede, la nota del caricamento lo dice.
                </span>
              )}
              {config.ermes && render(ERMES_OPERAZIONI('POD'))}
            </FormSection>
          )}
          {gas && (
            <FormSection title="Gas" className="mt-3" headingLevel={4}>
              {GAS.map(render)}
              {config.ermes && render(ERMES_OPERAZIONI('PDR'))}
            </FormSection>
          )}
          {(subentro || voltura || subentroGas || volturaGas) && (
            <>
              <FormSection title={subentro || subentroGas ? 'Subentro / riattivazione (modulo Ermes)' : 'Voltura (modulo Ermes)'} className="mt-4"
                description={(subentro || subentroGas
                  ? "Il portale aggiunge alla pratica la richiesta di subentro: questi dati la compilano. Nome, cognome, CF e residenza li prende dalla pratica; la nascita dalla carta d'identità o dall'anagrafica."
                  : "Il portale aggiunge alla pratica il modulo di voltura: questi dati lo compilano. Nome, cognome, CF e residenza li prende dalla pratica; la nascita dalla carta d'identità o dall'anagrafica; il tipo d'uso, se vuoto, da residente/non residente.")
                  + (tipo === 'AZIENDA' ? " Per un'azienda il sottoscritto e il dichiarante del modulo sono il rappresentante legale della scheda cliente (nome, cognome, codice fiscale, residenza): controlla che ci sia." : '')}>
                {(voltura || volturaGas) && ERMES_VOLTURA.map(render)}
                {(subentro ? ERMES_SUBENTRO_POD : voltura ? ERMES_SUBENTRO_POD.filter(f => f.key === 'TIPOLOGIA_POD' || f.key === 'TIPO_USO_POD') : []).map(render)}
                {(subentroGas || volturaGas) && ERMES_VOLTURA_GAS.map(render)}
                {ERMES_SUBENTRO_INTESTATARIO.map(render)}
              </FormSection>
              <FormSection title="Dichiarazione sostitutiva sull'immobile" className="mt-3" headingLevel={4}>
                {/* La voltura non ha scala/piano/interno dell'immobile né la dichiarazione di realizzazione. */}
                {(subentro ? ERMES_SUBENTRO_DICHIARAZIONE : ERMES_SUBENTRO_DICHIARAZIONE.filter(f => !['SCALA', 'PIANO', 'INTERNO', 'DICHIARAZIONE_REALIZZAZIONE', 'REALIZZAZIONE_NUMERO', 'REALIZZAZIONE_DATA', 'REALIZZAZIONE_COMUNE'].includes(f.key))).map(render)}
              </FormSection>
              {(subentro || subentroGas) && (
                <FormSection title="Solo forniture in Veneto" className="mt-3" headingLevel={4}>
                  {ERMES_SUBENTRO_VENETO.map(render)}
                </FormSection>
              )}
            </>
          )}
          {potenza && (
            <FormSection title="Aumento / diminuzione potenza (modulo Ermes, passo 8)" className="mt-4"
              description="Il portale aggiunge alla pratica la richiesta di nuova potenza: scegli la potenza fra quelle previste (oltre 17 kW o in media tensione indica i kW) e la tensione. Telefono ed email li prende dal cliente.">
              {ERMES_POTENZA.map(render)}
              {!subentro && ERMES_SUBENTRO_POD.filter(f => f.key === 'SCALA_POD' || f.key === 'PIANO_POD' || f.key === 'INTERNO_POD').map(render)}
            </FormSection>
          )}
          {config.ermes && luce && (
            <FormSection title="Allegato A: altri POD sullo stesso contratto (multisito)" className="mt-4"
              description="Il POD principale sta nella sezione Luce; qui gli altri, con il loro indirizzo. Il CRM compila l'Allegato A nella scheda «Moduli Ermes da firmare» (numero progressivo = numero di contratto Ermes, settore Annuale e tariffa B.T. già spuntati, nessuna opzione di fatturazione): firmato e timbrato in basso a destra si ricarica come foto o immagine, così finisce nei documenti aggiuntivi di Ermes. Per ogni POD aggiuntivo allega la sua bolletta.">
              {render(ALLEGATO_A_SITI)}
            </FormSection>
          )}
          {Array.from({ length: allegatoA }, (_, i) => i + 1).map(i => (
            <FormSection key={`allegato-a-${i}`} title={`POD aggiuntivo ${i}`} className="mt-3" headingLevel={4}>
              {ALLEGATO_A_SITO(i).map(render)}
            </FormSection>
          ))}
          {(subentro || voltura) && (
            <>
              <FormSection title="Avente titolo sull'immobile (se diverso dal cliente)" className="mt-3" headingLevel={4}
                description="Da compilare quando il cliente non è proprietario/intestatario: chi ha il titolo autorizza e firma digitalmente.">
                {ERMES_SUBENTRO_TITOLO.map(render)}
              </FormSection>
            </>
          )}
        </>
      ) : (
        <FormSection title="Attivazione" className="mt-4" description="Dove si attiva la linea.">
          {ATTIVAZIONE.map(render)}
          {config.technologies && v('TIPO_LINEA') !== 'MOBILE'
            && render({ key: 'TECNOLOGIA', label: 'Tecnologia', type: 'select', options: config.technologies.map(t => ({ value: t, label: t })) })}
          {(attivazione === 'PORTABILITA' || attivazione === 'MIGRAZIONE') && PORTABILITA.map(render)}
        </FormSection>
      )}

      {pdaSection}

      {modules.length > 0 && (
        <FormSection title="Opzioni con modulo" className="mt-4" columns={1}
          description="Se spunti un'opzione, scarica il modulo, fallo firmare e ricaricalo qui.">
          <div className="d-flex flex-wrap gap-3">{modules.map(m => render(check(m.flag, m.label)))}</div>
          {active.length > 0 && (
            <div className="ecrm-filetiles">
              {active.map(m => (
                <div key={m.flag} className="d-flex flex-column gap-1 align-items-start">
                  <FileTile label={m.label} file={moduleFiles[m.flag] ?? null} disabled={disabled ?? false}
                    onFile={f => onModuleFile(m.flag, f)} onClear={() => onModuleFile(m.flag, null)} />
                  {m.url
                    ? <a href={m.url} target="_blank" rel="noreferrer" className="md-typescale-body-small">Scarica modulo</a>
                    : <span className="md-typescale-body-small text-muted">Modulo dal portale</span>}
                </div>
              ))}
            </div>
          )}
        </FormSection>
      )}

      {config.payments.length > 0 && (
        <FormSection title="Pagamento" className="mt-4">
          {render({ key: 'MODO_PAGAMENTO', label: 'Modalità', type: 'radio', options: config.payments.map(p => ({ value: p, label: PAYMENT_LABELS[p] ?? p })) })}
          <div />
          {v('MODO_PAGAMENTO') === 'SDD' && SDD.map(render)}
          {v('MODO_PAGAMENTO') === 'SDD' && copyHolderButton}
        </FormSection>
      )}

      <FormSection title="Consensi e altro" className="mt-4">
        {render({ key: 'CONSENSO_PRIVACY', label: 'Consenso privacy', type: 'radio', options: o(['SI', 'Sì'], ['NO', 'No']) })}
        {render({ key: 'AUTORIZZAZIONE_RIPENSAMENTO', label: 'Attivazione prima dei 14 giorni di ripensamento', type: 'radio', options: o(['SI', 'Sì'], ['NO', 'No']) })}
        {config.ermes && (
          <span className="align-self-center">
            <HelpTip>Su Ermes (pagina «Altro») si sceglie sempre la prima voce: il cliente chiede l'esecuzione anche durante il ripensamento.</HelpTip>
          </span>
        )}
        {render(check('CONTRATTO_DA_TELEMARKETING', 'Contratto da telemarketing'))}
        {render(text('NOTE', 'Note per il portale', { maxLength: 40, hint: 'Massimo 40 caratteri.' }))}
      </FormSection>
    </>
  )
}
